import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { SalesforceService } from './salesforce.service'
import { environment } from '../../environments/environment'

/**
 * Plataformas soportadas según PCM
 */
export type PlatformName = 'COM' | 'WECOMMERCE' | 'ECOSENTINO' | 'SMARTCONNECT' | 'DISPLAYS' | 'CTOP' | 'SALESFORCE' | 'REGISTROVENTA'

/**
 * Servicio para gestionar la plataforma actual desde la cual se accede a la aplicación.
 * 
 * Este servicio determina la plataforma y la proporciona a los servicios de API
 * para que puedan filtrar los SKUs según la configuración de allowedPlatformsByMarket en PCM.
 * 
 * Plataformas:
 * - COM: Contacto directo (portal interno)
 * - WECOMMERCE: Tienda online WE
 * - ECOSENTINO: Ecosentino
 * - SMARTCONNECT: Salesforce SmartConnect (US)
 * - DISPLAYS: Portal de displays (PT)
 * - CTOP: CTOP (DE)
 * - SALESFORCE: Salesforce (IT)
 * - REGISTROVENTA: Registro de ventas (UK)
 */
@Injectable({
    providedIn: 'root'
})
export class PlatformService {
    
    // Observable para que los componentes puedan reaccionar a cambios de plataforma
    private platformSubject = new BehaviorSubject<PlatformName>('COM')
    platform$ = this.platformSubject.asObservable()
    
    constructor(private salesforce: SalesforceService) {
        this.detectPlatform()
    }

    /**
     * Obtiene la plataforma actual de forma síncrona
     */
    getPlatform(): PlatformName {
        return this.platformSubject.getValue()
    }

    /**
     * Establece la plataforma manualmente (para testing o inicialización externa).
     * Persiste en localStorage para que sobreviva a recargas.
     *
     * Las plataformas WECOMMERCE, ECOSENTINO y SMARTCONNECT deberían
     * llamar a este método durante su inicialización con su nombre correspondiente.
     */
    setPlatform(platform: PlatformName): void {
        console.log(`[PlatformService] Setting platform to: ${platform}`)
        this.platformSubject.next(platform)
        localStorage.setItem('wePlatform', platform)
    }

    /**
     * Establece la plataforma de forma contextual (sin persistir en localStorage).
     * Útil para secciones de la app que requieren un platform específico solo
     * mientras el usuario está en esa sección (ej: displays → 'DISPLAYS').
     */
    setContextualPlatform(platform: PlatformName): void {
        console.log(`[PlatformService] Setting contextual platform to: ${platform}`)
        this.platformSubject.next(platform)
    }

    /**
     * Detecta automáticamente la plataforma basándose en:
     * 1. Valor guardado en localStorage (si está disponible)
     * 2. Contexto de Salesforce Canvas (SMARTCONNECT)
     * 3. Variable de entorno (si está configurada)
     * 4. Por defecto: COM
     */
    private detectPlatform(): void {
        // 1. Verificar si hay una plataforma guardada en localStorage
        const savedPlatform = localStorage.getItem('wePlatform') as PlatformName | null
        if (savedPlatform) {
            console.log(`[PlatformService] Platform from localStorage: ${savedPlatform}`)
            this.platformSubject.next(savedPlatform)
            return
        }

        // 2. Verificar si está en contexto de Salesforce Canvas (SMARTCONNECT)
        if (this.salesforce.isCanvas()) {
            console.log('[PlatformService] Detected Salesforce Canvas - Platform: SMARTCONNECT')
            this.platformSubject.next('SMARTCONNECT')
            return
        }

        // 3. Verificar si hay una plataforma configurada en el environment (para web components)
        if (environment.webComponent && (environment as any).platform) {
            const envPlatform = (environment as any).platform as PlatformName
            console.log(`[PlatformService] Platform from environment: ${envPlatform}`)
            this.platformSubject.next(envPlatform)
            return
        }

        // 4. Por defecto: COM
        console.log('[PlatformService] Default platform: COM')
        this.platformSubject.next('COM')
    }
}
